import React from 'react';

import { devWarning } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import styled from '@tanium/react-emotion-9';
import { CloseIcon } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { css } from '@tanium/style-system';
import { ThemeSpace } from '@tanium/theme-data';

import { useTranslation } from './localization';
import PillBase, { PillBaseProps } from './PillBase';
import TooltipContent from './TooltipContent';

const StyledIconButton = styled(IconButton)({
  display: 'block',
});

export interface Props
  extends Pick<
    PillBaseProps,
    'keyForValue' | 'leadingIcon' | 'maxWidth' | 'customTooltipContent' | 'children' | 'size'
  > {
  /**
   * Handler for when a Pill's remove icon is clicked.
   */
  onRemove?: () => void;

  /**
   * Used for remove icon's `aria-label` attribute. Defaults to "Remove".
   */
  removeLabel?: string;
}

const RemoveButtonContainer = styled('span')(
  css({
    paddingLeft: ThemeSpace.minor[1],
  }),
);

const Pill = React.memo(
  ({
    children,
    customTooltipContent,
    keyForValue,
    leadingIcon,
    maxWidth,
    onRemove,
    removeLabel,
    size = 'lg',
  }: Props) => {
    devWarning(
      !!onRemove,
      'Using a Pill component without an onRemove prop has been deprecated. If no onRemove is desired use the Label component.',
    );

    const { t } = useTranslation();
    const removeButtonHeight = size === 'lg' ? 14 : 12; // icon is slightly smaller than button, this gets icon close to UX spec size

    return (
      <PillBase
        {...{
          keyForValue,
          leadingIcon,
          maxWidth,
          onRemove,
          removeLabel,
          alwaysEnableTooltip: !!customTooltipContent,
          size,
        }}
        tooltipContent={
          customTooltipContent ?? (
            <TooltipContent keyForValue={keyForValue}>{children}</TooltipContent>
          )
        }
        trailingContent={
          onRemove && (
            <RemoveButtonContainer>
              <StyledIconButton
                icon={CloseIcon}
                height={removeButtonHeight}
                width={removeButtonHeight}
                label={removeLabel || t('Remove')}
                onClick={(e) => {
                  onRemove();
                  e.stopPropagation();
                }}
              />
            </RemoveButtonContainer>
          )
        }
      >
        <Box as="span" fontWeight="bold">
          {children}
        </Box>
      </PillBase>
    );
  },
);
export default Pill;
